import PocketBase, { type RecordModel } from 'pocketbase';

function resolvePbUrl(): string | undefined {
  const pb = import.meta.env.VITE_PB_URL as string | undefined;
  if (pb && pb.trim() && !pb.includes('YOUR_') && pb.startsWith('http')) {
    return pb.replace(/\/$/, '');
  }
  // Transition: accept a PocketBase URL still set under the old env name.
  const legacy = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (
    legacy &&
    /pb\.|8090|pocketbase/i.test(legacy) &&
    legacy.startsWith('http')
  ) {
    return legacy.replace(/\/$/, '');
  }
  return undefined;
}

const url = resolvePbUrl();

let client: PocketBase | null = null;

export type User = {
  id: string;
  email?: string;
  user_metadata: { display_name?: string };
};

export type Session = {
  user: User;
  access_token: string;
};

/** Kept for auth callback type compatibility after the Supabase cutover. */
export type EmailOtpType =
  | 'signup'
  | 'invite'
  | 'magiclink'
  | 'recovery'
  | 'email_change'
  | 'email';

export function isSupabaseConfigured(): boolean {
  return Boolean(url);
}

/** @deprecated Alias — PocketBase is configured when this is true. */
export const isPbConfigured = isSupabaseConfigured;

export function getSupabaseUrl(): string | undefined {
  return url;
}

export function getPBUrl(): string | undefined {
  return url;
}

export function getPB(): PocketBase {
  if (!url) {
    throw new Error(
      'PocketBase is not configured. Set VITE_PB_URL (e.g. https://pb.jpsariz.com).',
    );
  }
  if (!client) {
    client = new PocketBase(url);
    client.autoCancellation(false);
  }
  return client;
}

/**
 * Former Supabase client accessor. Runtime code should use {@link getPB}.
 * Throws so leftover call sites fail loudly during the cutover.
 */
export function getSupabase(): never {
  throw new Error(
    'getSupabase() is removed. Use getPB() from @bangle.io/supabase (PocketBase).',
  );
}

function mapUser(record: RecordModel | null | undefined): User | null {
  if (!record) return null;
  return {
    id: record.id,
    email: typeof record.email === 'string' ? record.email : undefined,
    user_metadata: {
      display_name:
        typeof record.display_name === 'string'
          ? record.display_name
          : undefined,
    },
  };
}

export function mapSessionFromAuthStore(): Session | null {
  const pb = getPB();
  const record = pb.authStore.record;
  const user = mapUser(record);
  if (!user || !pb.authStore.token) return null;
  // Treat expired JWTs as signed-out so chrome does not show a zombie email.
  if (!pb.authStore.isValid) return null;
  return {
    user,
    access_token: pb.authStore.token,
  };
}

export async function getSession(): Promise<Session | null> {
  if (!isSupabaseConfigured()) return null;
  return mapSessionFromAuthStore();
}

/**
 * Validate the local PocketBase JWT with the server and refresh it.
 * Clears authStore on failure so a dead token cannot look "signed-in"
 * while membership lists silently return empty (and prune local notebooks).
 */
export async function ensureFreshSession(): Promise<Session | null> {
  if (!isSupabaseConfigured()) return null;
  const pb = getPB();
  if (!pb.authStore.token) return null;
  try {
    if (!pb.authStore.isValid) {
      pb.authStore.clear();
      return null;
    }
    await pb.collection('users').authRefresh();
    return mapSessionFromAuthStore();
  } catch {
    pb.authStore.clear();
    return null;
  }
}

export async function getUser(): Promise<User | null> {
  const session = await getSession();
  return session?.user ?? null;
}

export function onAuthStateChange(
  cb: (session: Session | null) => void,
): () => void {
  if (!isSupabaseConfigured()) {
    cb(null);
    return () => {};
  }
  const pb = getPB();
  cb(mapSessionFromAuthStore());
  return pb.authStore.onChange(() => {
    cb(mapSessionFromAuthStore());
  });
}

export function newLegacyId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function randomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = (Math.random() * 256) | 0;
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** PocketBase returns "YYYY-MM-DD HH:MM:SS.mmmZ"; normalize toward ISO-8601. */
export function pbDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return new Date().toISOString();
  return v.includes('T') ? v : v.replace(' ', 'T');
}

export type { RecordModel };
